import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Spinner } from '@mbbe/ui';
import { useAuth } from './contexts/AuthContext';

const Login = React.lazy(() => import('./pages/Login'));
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const Projects = React.lazy(() => import('./pages/Projects'));
const Analytics = React.lazy(() => import('./pages/Analytics'));
const ProjectAnalytics = React.lazy(() => import('./pages/ProjectAnalytics'));
const EngagementAnalytics = React.lazy(() => import('./pages/EngagementAnalytics'));
const VisitorLookup = React.lazy(() => import('./pages/VisitorLookup'));
const Archive = React.lazy(() => import('./pages/Archive'));
const Retention = React.lazy(() => import('./pages/Retention'));
const DesignSystem = React.lazy(() => import('./pages/DesignSystem'));
const QuoteRequests = React.lazy(() => import('./pages/QuoteRequests'));
const Customers = React.lazy(() => import('./pages/Customers'));
const SupportIssues = React.lazy(() => import('./pages/SupportIssues'));
const Templates = React.lazy(() => import('./pages/Templates'));
const Staff = React.lazy(() => import('./pages/Staff'));
const StaffTasks = React.lazy(() => import('./pages/StaffTasks'));
const StaffPayments = React.lazy(() => import('./pages/StaffPayments'));
const StaffPayRules = React.lazy(() => import('./pages/StaffPayRules'));
const StaffOffDays = React.lazy(() => import('./pages/StaffOffDays'));
const Settings = React.lazy(() => import('./pages/Settings'));
const Notifications = React.lazy(() => import('./pages/Notifications'));
const McpDrafts = React.lazy(() => import('./pages/McpDrafts'));

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading, isAdmin } = useAuth();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-dark-900">
        <Spinner size="lg" className="text-brand-500" />
      </div>
    );
  }

  if (!user || !isAdmin) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function App() {
  return (
    <React.Suspense fallback={
      <div className="flex items-center justify-center min-h-screen bg-dark-900">
        <Spinner size="lg" className="text-brand-500" />
      </div>
    }>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/projects"
          element={
            <ProtectedRoute>
              <Projects />
            </ProtectedRoute>
          }
        />
        <Route
          path="/analytics"
          element={
            <ProtectedRoute>
              <Analytics />
            </ProtectedRoute>
          }
        />
        <Route
          path="/analytics/project/:id"
          element={
            <ProtectedRoute>
              <ProjectAnalytics />
            </ProtectedRoute>
          }
        />
        <Route
          path="/visitor/:ip"
          element={
            <ProtectedRoute>
              <VisitorLookup />
            </ProtectedRoute>
          }
        />
        <Route
          path="/archive"
          element={
            <ProtectedRoute>
              <Archive />
            </ProtectedRoute>
          }
        />
        <Route
          path="/retention"
          element={
            <ProtectedRoute>
              <Retention />
            </ProtectedRoute>
          }
        />
        <Route
          path="/design-system"
          element={
            <ProtectedRoute>
              <DesignSystem />
            </ProtectedRoute>
          }
        />
        <Route
          path="/quotes"
          element={
            <ProtectedRoute>
              <QuoteRequests />
            </ProtectedRoute>
          }
        />
        <Route
          path="/quotes/:id"
          element={
            <ProtectedRoute>
              <QuoteRequests />
            </ProtectedRoute>
          }
        />
        <Route
          path="/engagement"
          element={
            <ProtectedRoute>
              <EngagementAnalytics />
            </ProtectedRoute>
          }
        />
        <Route
          path="/customers"
          element={
            <ProtectedRoute>
              <Customers />
            </ProtectedRoute>
          }
        />
        <Route
          path="/customers/:id"
          element={
            <ProtectedRoute>
              <Customers />
            </ProtectedRoute>
          }
        />
        <Route
          path="/issues"
          element={
            <ProtectedRoute>
              <SupportIssues />
            </ProtectedRoute>
          }
        />
        <Route
          path="/issues/:id"
          element={
            <ProtectedRoute>
              <SupportIssues />
            </ProtectedRoute>
          }
        />
        <Route
          path="/templates"
          element={
            <ProtectedRoute>
              <Templates />
            </ProtectedRoute>
          }
        />
        <Route
          path="/templates/:id"
          element={
            <ProtectedRoute>
              <Templates />
            </ProtectedRoute>
          }
        />
        <Route
          path="/staff"
          element={
            <ProtectedRoute>
              <Staff />
            </ProtectedRoute>
          }
        />
        <Route
          path="/staff/:id"
          element={
            <ProtectedRoute>
              <Staff />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tasks"
          element={
            <ProtectedRoute>
              <StaffTasks />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tasks/:id"
          element={
            <ProtectedRoute>
              <StaffTasks />
            </ProtectedRoute>
          }
        />
        <Route
          path="/payments"
          element={
            <ProtectedRoute>
              <StaffPayments />
            </ProtectedRoute>
          }
        />
        <Route
          path="/pay-rules"
          element={
            <ProtectedRoute>
              <StaffPayRules />
            </ProtectedRoute>
          }
        />
        <Route
          path="/off-days"
          element={
            <ProtectedRoute>
              <StaffOffDays />
            </ProtectedRoute>
          }
        />
        <Route
          path="/off-days/:id"
          element={
            <ProtectedRoute>
              <StaffOffDays />
            </ProtectedRoute>
          }
        />
        <Route
          path="/notifications"
          element={
            <ProtectedRoute>
              <Notifications />
            </ProtectedRoute>
          }
        />
        <Route
          path="/mcp-drafts"
          element={
            <ProtectedRoute>
              <McpDrafts />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <Settings />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </React.Suspense>
  );
}

export default App;
